import axios from 'axios';
import store from '@/store';
import router from "@/router";
import { translate } from './translate';
import authUser from './authUser';
import Cookies from 'js-cookie';
import crsfHandler from "@/composables/csrfHandler";
import apiBaseUrl from '@/composables/apiUrl';
import { attachCorrelationId, captureCorrelationError, captureCorrelationId } from '@/composables/correlationId';
import { appendQueryParameters } from '@/composables/queryParameters.mjs';

const requestHandler = axios.create({
    baseURL: `${apiBaseUrl}/`,
    headers: {
        'Accept': 'application/json',
    },
    withCredentials: true,
    withXSRFToken: true
});

requestHandler.interceptors.request.use((config) => {
    attachCorrelationId(config);
    const token = store.getters.getToken; 
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    const activeCompanyId = store.getters.getActiveCompanyId;
    if (activeCompanyId) {
        config.headers['X-Company-Id'] = activeCompanyId;
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});

requestHandler.interceptors.request.use(
    config => {
        return config;
      },
      error => {
        return Promise.reject(error);
    }
);

requestHandler.interceptors.response.use(
    response => captureCorrelationId(response),
    error => {
        captureCorrelationError(error);
        if (error.response == undefined) {
            return {
                success: false,
                data: {},
                message: translate('a problem occurred with your application, but do not worry, we are already looking into it', 'ucfirst'),
                status: 900,
            };
        }else{
            throw error;
        }
    }
);

/**
 * Functions
 */
requestHandler.baseUrl = function getBaseUrl(){
    return requestHandler.defaults.baseURL;
}
requestHandler.makeUrl = function makeUrl(uri){
    return requestHandler.defaults.baseURL + uri;
}
/**
 * Perform an AXIOS request
 * @param {String} url 
 * @param {Object} parameters 
 * @param {String} method - 'post', 'get', 'put', 'patch' or 'delete'
 * @returns {
 *  success: Boolean,
 *  data: requestResult
 *  status: integer
 * }
 */
requestHandler.executeRequest = async function executeRequest(url, parameters = {}, method = 'post', options = {}){
        let resultFormat = {
        success: true,
        data: {},
        message: '',
        status: 200,
        correlationId: null,
    };
    parameters['user_lang'] = store.getters.getUserLang;
    let parametersAsArray = Object.keys(parameters);
    try {
        let response = null;
        switch(method){
            case 'post':
                response = await this.post(url, parameters, options.axiosConfig || {});
            break;
            case 'get':
                if(parametersAsArray.length > 0)
                    url = appendQueryParameters(url, parameters);
                response = await this.get(url, options.axiosConfig || {});
            break;
            case 'put':
                response = await this.put(url, parameters, options.axiosConfig || {});
            break;
            case 'patch':
                response = await this.patch(url, parameters, options.axiosConfig || {});
            break;
            case 'delete':
                response = await this.delete(url, {params: parameters, ...(options.axiosConfig || {})});
            break;
        }
        if(response.status == 900){
            store.dispatch('saveSessionMessage', {
                message: translate('a problem occurred with your application, but do not worry, we are already looking into it', 'ucfirst'), 
                messageType: 'error'
            });
            return {
                success: false,
                data: {},
                message: 'error',
                status: 900,
            };
        }
        resultFormat.data = response.data;
        resultFormat.status = response.status;
        resultFormat.correlationId = response.correlationId;
        if(response.data.message !== undefined)
            resultFormat.message = translate(response.data.message);
        return resultFormat;
    } catch (error) {
        const defaultErrorMessage = translate('unexpected error, please try again later', 'ucfirst');
        resultFormat.success = false;
        resultFormat.data = "";
        resultFormat.status = 500;
        if(error !== undefined && error.response !== undefined){
            resultFormat.data = error.response.data;
            resultFormat.status = error.response.status;
            resultFormat.correlationId = error.correlationId;
            if(error.response.data.message !== undefined)
                resultFormat.message = translate(error.response.data.message);
            if(resultFormat.status == 419 && options.handleUnauthorized !== false)
                await this.expireSession(translate('login expired, perform login again please'));
        }
        if(resultFormat.message == '')
            resultFormat.message = defaultErrorMessage;
        if(resultFormat.status == 401 && resultFormat.data.message != undefined && resultFormat.data.message == 'Unauthenticated.'){
            if(options.handleUnauthorized !== false){
                await this.expireSession();
            }else if(!store.getters.isLoggedIn){
                Cookies.remove('XSRF-TOKEN');
                Cookies.remove('jobifull_session');
                Cookies.remove('last_login_as');
                store.dispatch('cleanAuthData');
            }
        }
        if(['CSRF token mismatch.','Route [login] not defined.'].includes(resultFormat.message)){
            Cookies.remove('XSRF-TOKEN');
            Cookies.remove('jobifull_session');
            Cookies.remove('last_login_as');
            store.dispatch('cleanAuthData');
            await crsfHandler.getCRSF(true);
            return await this.executeRequest(url, parameters, method, options);
        }
        return resultFormat;
    }
}
let sessionExpirationPromise = null;
requestHandler.expireSession = async function expireSession(message = translate('authentication expired')){
    if(sessionExpirationPromise)
        return sessionExpirationPromise;
    sessionExpirationPromise = (async () => {
        const hadSession = Boolean(store.getters.getToken || store.getters.isLoggedIn);
        await authUser.logout(true);
        if(hadSession){
            store.dispatch('saveSessionMessage', {
                message,
                messageType: 'error'
            });
        }
        if(router.currentRoute.value.path !== '/login')
            await router.replace({path: '/login'});
    })();
    return sessionExpirationPromise;
}
requestHandler.resetSessionExpiration = function resetSessionExpiration(){
    sessionExpirationPromise = null;
}
requestHandler.redirect = async function goTo(url = '/'){
    return router.push({path: url});
}
requestHandler.replace = async function replaceWith(url = '/'){
    return router.replace({path: url});
}
requestHandler.redirectByProfile = async function redirectByProfile(){
    const routeToGo = store.getters.isLoggedIn && store.getters.isOnlyPersonProfile ? '/dashboard/profile' : '/dashboard/' + store.getters.getLoggedAs + '/' + store.getters.getUserSlug;
    requestHandler.redirect(routeToGo);
}
requestHandler.fetchOnPublicData = async function fetchOnPublicData(url = ''){
    if(url == '')
        return false;
    let gatheredData = false;
    try {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `/data/${url}`, false);
        xhr.send();
        if (xhr.status === 200) {
            gatheredData = JSON.parse(xhr.responseText);
        }
    } catch (error) {
        return gatheredData;
    }
    return gatheredData;
}

export default requestHandler;
